/*============================================================================
  Personal Gourmet — theme.js
  Vanilla JS + custom elements. All globals namespaced under window.PG.
  Cross-component communication uses CustomEvents dispatched on `document`.
============================================================================*/
(function () {
  'use strict';

  const PG = (window.PG = window.PG || {});
  PG.routes = window.routes || {};
  PG.strings = window.PGStrings || {};

  const money = (cents) => {
    const value = (cents / 100).toFixed(2);
    return PG.moneyFormat ? PG.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, value) : '$' + value;
  };

  /*------------------------------------------------------------
    Cart API helpers
  ------------------------------------------------------------*/
  PG.cart = {
    async add(items) {
      const res = await fetch(`${PG.routes.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart');
      }
      return res.json();
    },
    async change(line, quantity) {
      const res = await fetch(`${PG.routes.cart_change_url || '/cart/change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      if (!res.ok) throw new Error('Could not update cart');
      return res.json();
    },
    async get() {
      const res = await fetch(`${PG.routes.cart_url || '/cart'}.js`);
      return res.json();
    },
  };

  /*------------------------------------------------------------
    Toast
  ------------------------------------------------------------*/
  let toastEl, toastTimer;
  PG.toast = function (message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'cart-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML =
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg><span></span>';
    toastEl.querySelector('span').textContent = message;
    requestAnimationFrame(() => toastEl.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 2600);
  };

  PG.refreshCartCount = async function () {
    try {
      const cart = await PG.cart.get();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        const btn = el.closest('.header__cart');
        if (btn) btn.classList.toggle('has-items', cart.item_count > 0);
      });
      document.dispatchEvent(new CustomEvent('pg:cart:updated', { detail: { cart } }));
    } catch (e) {
      /* silent */
    }
  };

  /*------------------------------------------------------------
    Sticky header
  ------------------------------------------------------------*/
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 40);
      this.onScroll();
      window.addEventListener('scroll', this.onScroll, { passive: true });

      this.bar = this.querySelector('.announcement-bar');
      this.inner = this.querySelector('.header__inner');
      this.toggle = this.querySelector('[data-menu-toggle]');
      this.menu = this.querySelector('[data-mobile-menu]');

      /* --header-height drives every page's top offset and the mobile menu's
         height. Measure the real bar + nav rather than trusting the CSS
         fallback: the announcement text wraps to two lines on narrow phones. */
      this.syncHeight = () => {
        const h = (this.bar ? this.bar.offsetHeight : 0) + (this.inner ? this.inner.offsetHeight : 0);
        if (h > 0) document.documentElement.style.setProperty('--header-height', h + 'px');
        if (this.menu && this.menu.classList.contains('is-open')) this.sizeMenu();
      };
      this.syncHeight();
      // Webfonts land after first paint and can change the bar's height.
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.syncHeight);
      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(this.syncHeight);
        if (this.bar) this.ro.observe(this.bar);
        if (this.inner) this.ro.observe(this.inner);
      }
      this.onResize = () => this.syncHeight();
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('orientationchange', this.onResize);

      if (this.toggle && this.menu) this.initMenu();

      // Logo hover spin
      this.querySelectorAll('.logo-nav').forEach((logo) => {
        logo.addEventListener('mouseenter', () => {
          logo.classList.remove('logo-spin');
          void logo.offsetWidth;
          logo.classList.add('logo-spin');
        });
        logo.addEventListener('animationend', (e) => {
          if (e.animationName === 'logoHoverSpin') logo.classList.remove('logo-spin');
        });
      });
    }

    /* The menu is inside a position:fixed header, so it can never be reached by
       scrolling the page — it has to cap itself to the space left below the
       header and scroll internally. */
    sizeMenu() {
      const top = this.menu.getBoundingClientRect().top;
      const avail = (window.visualViewport ? window.visualViewport.height : window.innerHeight) - top;
      this.menu.style.maxHeight = Math.max(200, Math.floor(avail)) + 'px';
    }

    setMenu(open) {
      this.menu.classList.toggle('is-open', open);
      this.toggle.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('menu-open', open);
      if (open) {
        this.sizeMenu();
        this.menu.scrollTop = 0;
      } else {
        this.menu.style.maxHeight = '';
      }
    }

    initMenu() {
      this.toggle.addEventListener('click', () => {
        this.setMenu(!this.menu.classList.contains('is-open'));
      });
      // Tapping a link navigates, but close anyway so a cancelled/hash nav
      // doesn't leave the panel covering the page.
      this.menu.addEventListener('click', (e) => {
        if (e.target.closest('a')) this.setMenu(false);
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.menu.classList.contains('is-open')) {
          this.setMenu(false);
          this.toggle.focus();
        }
      });
      document.addEventListener('click', (e) => {
        if (!this.menu.classList.contains('is-open')) return;
        if (!this.contains(e.target)) this.setMenu(false);
      });
      // Desktop layout takes over: drop any leftover open state.
      this.mq = window.matchMedia('(min-width: 769px)');
      const onMq = (e) => { if (e.matches) this.setMenu(false); };
      if (this.mq.addEventListener) this.mq.addEventListener('change', onMq);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', () => {
          if (this.menu.classList.contains('is-open')) this.sizeMenu();
        });
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
      if (this.ro) this.ro.disconnect();
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /*------------------------------------------------------------
    Collection facets — on mobile these collapse to a scrollable
    chip row, so bring the active category into view.
  ------------------------------------------------------------*/
  const revealActiveFacet = () => {
    const facets = document.querySelector('.facets');
    if (!facets) return;
    const active = facets.querySelector('.facets__link.is-active');
    if (!active || facets.scrollWidth <= facets.clientWidth) return;
    // 'nearest' is a no-op when the chip is already on screen, so the Categories
    // label doesn't get dragged off the left edge for no reason. Wait for the
    // webfonts first — measuring before they swap gives the wrong chip widths.
    active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };
  document.addEventListener('DOMContentLoaded', () => {
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(revealActiveFacet);
    else revealActiveFacet();
  });

  /*------------------------------------------------------------
    Quantity input
  ------------------------------------------------------------*/
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) =>
        btn.addEventListener('click', () => {
          const step = btn.dataset.action === 'increase' ? 1 : -1;
          const min = parseInt(this.input.min || '1', 10);
          const next = Math.max(min, (parseInt(this.input.value, 10) || min) + step);
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /*------------------------------------------------------------
    Add-to-cart buttons on product cards (data-product-add)
  ------------------------------------------------------------*/
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-product-add]');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.dataset.productAdd;
    if (!variantId) return;
    const original = btn.textContent;
    btn.disabled = true;
    try {
      await PG.cart.add([{ id: Number(variantId), quantity: 1 }]);
      btn.classList.add('is-added');
      btn.textContent = PG.strings.added || '✓ Added';
      PG.toast(PG.strings.addedToCart || 'Added to cart');
      await PG.refreshCartCount();
      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.textContent = original;
        btn.disabled = false;
      }, 1500);
    } catch (err) {
      btn.textContent = original;
      btn.disabled = false;
      PG.toast(err.message || 'Could not add to cart');
    }
  });

  /*------------------------------------------------------------
    Product form (variant picker + add to cart)
  ------------------------------------------------------------*/
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.variantInput = this.querySelector('[name="id"]');
      this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
      this.priceTarget = this.querySelector('[data-price]');
      this.unitTarget = this.querySelector('[data-variant-unit]');
      this.perServingTarget = this.querySelector('[data-per-serving]');
      this.submitBtn = this.querySelector('[data-add-to-cart]');

      this.querySelectorAll('[data-option-input]').forEach((input) =>
        input.addEventListener('change', () => this.onOptionChange())
      );

      if (this.form) {
        this.form.addEventListener('submit', (e) => this.onSubmit(e));
      }
    }

    selectedOptions() {
      const opts = [];
      this.querySelectorAll('[data-option-position]').forEach((group) => {
        const input = group.querySelector('[data-option-input]');
        if (!input) return;
        // <select> since the pack-size options came back; radios kept working
        // for any option group still rendered as chips.
        const value =
          input.tagName === 'SELECT'
            ? input.value
            : group.querySelector('[data-option-input]:checked')?.value;
        if (value != null) opts[parseInt(group.dataset.optionPosition, 10) - 1] = value;
      });
      return opts;
    }

    onOptionChange() {
      const selected = this.selectedOptions();
      const match = this.variants.find((v) =>
        v.options.every((opt, i) => opt === selected[i])
      );
      if (!match) {
        if (this.submitBtn) {
          this.submitBtn.disabled = true;
          this.submitBtn.textContent = PG.strings.unavailable || 'Unavailable';
        }
        return;
      }
      this.variantInput.value = match.id;
      if (this.priceTarget) {
        const compare = match.compare_at_html ? '<s>' + match.compare_at_html + '</s> ' : '';
        // Each option is its own pack size, so the "per box" / "each" suffix
        // travels with the variant rather than being fixed on the product.
        const unit = match.unit_label || 'per box';
        const unitHtml =
          match.servings > 1
            ? '<span class="product__price-unit" data-price-unit> ' + unit + '</span>'
            : '';
        this.priceTarget.innerHTML = compare + (match.price_html || money(match.price)) + unitHtml;
      }
      if (this.unitTarget && match.title) this.unitTarget.textContent = match.title;
      if (this.perServingTarget) {
        // Server-rendered value when we have one (correct rounding + currency
        // format); otherwise fall back to computing it here.
        const pps =
          match.per_serving_html ||
          (match.servings > 1 ? money(Math.round(match.price / match.servings)) : null);
        if (pps) {
          this.perServingTarget.textContent = '(' + pps + ' per serving)';
          this.perServingTarget.dataset.servings = match.servings;
          this.perServingTarget.hidden = false;
        } else {
          // An "Each" option — its price already IS the per-serving price.
          this.perServingTarget.textContent = '';
          this.perServingTarget.hidden = true;
        }
      }
      if (this.submitBtn) {
        this.submitBtn.disabled = !match.available;
        this.submitBtn.textContent = match.available
          ? PG.strings.addToCart || 'Add to Cart'
          : PG.strings.soldOut || 'Sold Out';
      }
      // sync URL for shareable variant
      if (match.id && history.replaceState) {
        const url = new URL(window.location);
        url.searchParams.set('variant', match.id);
        history.replaceState({}, '', url);
      }
    }

    async onSubmit(e) {
      e.preventDefault();
      if (!this.variantInput.value) return;
      const qtyEl = this.querySelector('[name="quantity"]');
      const quantity = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;
      this.submitBtn.disabled = true;
      const original = this.submitBtn.textContent;
      try {
        await PG.cart.add([{ id: Number(this.variantInput.value), quantity }]);
        this.submitBtn.textContent = PG.strings.addedToCart || '✓ Added to Cart';
        PG.toast(PG.strings.addedToCart || 'Added to cart');
        await PG.refreshCartCount();
        setTimeout(() => {
          this.submitBtn.textContent = original;
          this.submitBtn.disabled = false;
        }, 2000);
      } catch (err) {
        this.submitBtn.disabled = false;
        PG.toast(err.message || 'Could not add to cart');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /*------------------------------------------------------------
    Media gallery (thumbnails swap main image)
  ------------------------------------------------------------*/
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) =>
        thumb.addEventListener('click', () => {
          const src = thumb.dataset.galleryThumb;
          const alt = thumb.querySelector('img')?.alt || '';
          if (this.main && src) {
            this.main.src = src;
            this.main.alt = alt;
          }
          this.querySelectorAll('[data-gallery-thumb]').forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        })
      );
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /*------------------------------------------------------------
    Collection filtering — client-side search + sort over rendered cards.
    (Category links navigate to the matching collection URL via <a>.)
  ------------------------------------------------------------*/
  class CollectionTools extends HTMLElement {
    connectedCallback() {
      this.grid = document.querySelector('[data-product-grid]');
      this.cards = this.grid ? Array.from(this.grid.children) : [];
      this.search = this.querySelector('[data-search]');
      this.sort = this.querySelector('[data-sort]');
      this.countTarget = document.querySelector('[data-result-count]');

      if (this.search) this.search.addEventListener('input', () => this.apply());
      if (this.sort) this.sort.addEventListener('change', () => this.apply());
    }

    apply() {
      const q = (this.search?.value || '').trim().toLowerCase();
      let visible = 0;
      this.cards.forEach((card) => {
        const name = (card.dataset.title || '').toLowerCase();
        const show = !q || name.includes(q);
        card.style.display = show ? '' : 'none';
        if (show) visible++;
      });

      const sort = this.sort?.value;
      if (sort && sort !== 'manual') {
        const sorted = [...this.cards].sort((a, b) => {
          if (sort === 'price-asc') return Number(a.dataset.price) - Number(b.dataset.price);
          if (sort === 'price-desc') return Number(b.dataset.price) - Number(a.dataset.price);
          if (sort === 'title-asc') return (a.dataset.title || '').localeCompare(b.dataset.title || '');
          return 0;
        });
        sorted.forEach((c) => this.grid.appendChild(c));
      }

      if (this.countTarget) {
        this.countTarget.textContent = `${visible} ${visible === 1 ? 'product' : 'products'} available`;
      }
    }
  }
  customElements.define('collection-tools', CollectionTools);

  /*------------------------------------------------------------
    Cart page line-item quantity + remove
  ------------------------------------------------------------*/
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-quantity-input]');
        if (input) this.updateLine(input.dataset.line, input.value);
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-remove-line]');
        if (remove) {
          e.preventDefault();
          this.updateLine(remove.dataset.removeLine, 0);
        }
      });
    }
    async updateLine(line, quantity) {
      this.classList.add('is-loading');
      try {
        await PG.cart.change(Number(line), Number(quantity));
        window.location.reload();
      } catch (err) {
        PG.toast(err.message || 'Could not update cart');
        this.classList.remove('is-loading');
      }
    }
  }
  customElements.define('cart-items', CartItems);

  /*------------------------------------------------------------
    Size & Pack picker — each pack size is its own listing, so
    choosing one navigates to that product page.
  ------------------------------------------------------------*/
  document.addEventListener('change', (e) => {
    const picker = e.target.closest('[data-size-picker]');
    if (picker && picker.value) window.location.href = picker.value;
  });

  // Initialise header count on load
  document.addEventListener('DOMContentLoaded', PG.refreshCartCount);
})();
